import React from "react";
import PropTypes from "prop-types";
import { fmtEur } from "./format";
import "../../../../components/ui-components/DataTable.css";

// StatusPill renders a colored pill for the budget status.
// green=success, amber=warning, red=danger, neutral=surface
function StatusPill({ status }) {
  const s = (status || "neutral").toLowerCase();
  let cls =
    "inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ";
  if (s === "green") {
    cls += "bg-success-soft text-success";
  } else if (s === "amber") {
    cls += "bg-warning-soft text-warning";
  } else if (s === "red") {
    cls += "bg-danger-soft text-danger";
  } else {
    cls += "bg-surface-2 text-text-2";
  }
  const label = s.charAt(0).toUpperCase() + s.slice(1);
  return <span className={cls}>{label}</span>;
}
StatusPill.propTypes = { status: PropTypes.string };

// NoCap badge shown in the status column for owner rows.
function NoCapBadge() {
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium bg-surface-2 text-text-3 italic">
      no cap
    </span>
  );
}

export default function BudgetRankTable({
  rows,
  loading,
  selected,
  onSelect,
  // Also accept shell's naming (data / isLoading)
  data,
  isLoading,
}) {
  const effectiveRows = rows ?? data ?? [];
  const effectiveLoading = loading ?? isLoading ?? false;

  // Sort: capped budgets first (by pct desc), then owner rows (by projected desc).
  // Backend should already rank; this is a defensive guard for stale data.
  const sorted = [...effectiveRows].sort((a, b) => {
    const aIsOwner = a.kind === "owner";
    const bIsOwner = b.kind === "owner";
    if (aIsOwner !== bIsOwner) return aIsOwner ? 1 : -1;
    if (aIsOwner) return (b.projected ?? 0) - (a.projected ?? 0);
    return (b.pct ?? 0) - (a.pct ?? 0);
  });

  if (effectiveLoading) {
    return (
      <div className="px-4 py-8 text-center text-sm text-text-3">
        Loading budgets…
      </div>
    );
  }

  if (!sorted || sorted.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-sm text-text-3">No budgets available.</p>
        <p className="mt-1 text-xs text-text-3">
          Select a horizon to load budget data.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-card border border-card-bd bg-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>
              Budget
            </th>
            <th>
              Projected
            </th>
            <th>
              Cap
            </th>
            <th>
              % of cap
            </th>
            <th>
              Status
            </th>
            <th>
              Days to breach
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isOwner = row.kind === "owner";
            const isSelected = selected === row.id;
            const overBudget = !isOwner && (row.pct ?? 0) > 1;

            return (
              <tr
                key={row.id}
                onClick={() => onSelect && onSelect(row.id)}
                className={`cursor-pointer transition-colors hover:bg-surface-2 ${
                  isSelected ? "bg-primary-soft hover:bg-primary-soft" : ""
                }`}
              >
                {/* Name */}
                <td>
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${
                        isSelected ? "text-primary" : "text-text"
                      }`}
                    >
                      {row.name}
                    </span>
                  </div>
                </td>

                {/* Projected spend */}
                <td className="tabular-nums text-text-2">
                  {fmtEur(row.projected)}
                </td>

                {/* Cap */}
                <td className="tabular-nums text-text-2">
                  {isOwner ? "—" : fmtEur(row.cap)}
                </td>

                {/* % of cap — red when over budget; "—" for owners */}
                <td className="tabular-nums">
                  {isOwner ? (
                    <span className="text-text-3">—</span>
                  ) : (
                    <span
                      className={`font-medium ${
                        overBudget ? "text-danger" : "text-text-2"
                      }`}
                    >
                      {row.pct != null ? `${Math.round(row.pct * 100)}%` : "—"}
                    </span>
                  )}
                </td>

                {/* Status pill / no-cap badge */}
                <td>
                  {isOwner ? <NoCapBadge /> : <StatusPill status={row.status} />}
                </td>

                {/* Days to breach */}
                <td className="tabular-nums text-text-2">
                  {isOwner ? "—" : (row.days_to_breach != null ? row.days_to_breach : "—")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

BudgetRankTable.propTypes = {
  rows: PropTypes.array,
  data: PropTypes.array,
  loading: PropTypes.bool,
  isLoading: PropTypes.bool,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func,
};
